import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { from, of, throwError } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { AESService } from 'src/services/data/aes.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import {
  cancelAESExportReport,
  cancelAESExportReportFailure,
  cancelAESExportReportSuccess,
  createAESExportReport,
  createAESExportReportFailure,
  createAESExportReportSuccess,
  createAESExportRequestsInBatch,
  createAESInBatchFailure,
  createAESInBatchSuccess,
  setAesResponseStatus,
  setAesResponseStatusFailure,
  setAesResponseStatusSuccess
} from './aes.actions'

@Injectable()
export class AesEffects {
  private actions$ = inject(Actions)
  private aes = inject(AESService)
  private toaster = inject(ToasterService)


  createAESExportReport$ = createEffect(() => this.actions$.pipe(
    ofType(createAESExportReport),
    switchMap(action =>
      from(this.aes.create(action.dealId, action.aesNumberAlreadyAssigned, action.aesConsigneeSource)).pipe(
        map(aes => createAESExportReportSuccess({
            aes,
        })),
        tap(({ aes }) => this.toaster.success(`Deal ${aes.deal_id} is sent successfully to AES`)),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to create AES export report', error)
          this.toaster.error('Unable to create AES export report', error)
          return of(createAESExportReportFailure({ error }))
        })))))

  cancelAESExportReport$ = createEffect(() => this.actions$.pipe(
    ofType(cancelAESExportReport),
    switchMap(action =>
      from(this.aes.cancel(action.reportId, action.dealId, action.reason)).pipe(
        map(aes => cancelAESExportReportSuccess({
            aes,
        })),
        tap(({ aes }) => this.toaster.success(`AES commodity export report cancellation requested for ${aes?.deal_id}`)),
        catchError(error => {
          if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
          console.error('Unable to cancel AES export report', error)
          this.toaster.error('Unable to cancel AES export report', error)
          return of(cancelAESExportReportFailure({ error }))
        })))))

    createAESExportRequestsInBatch$ = createEffect(() => this.actions$.pipe(
      ofType(createAESExportRequestsInBatch),
      switchMap(action =>
        from(this.aes.createInBatch(action.dealIds, action.aesNumberAlreadyAssigned)).pipe(
          map(result => createAESInBatchSuccess(result)),
          tap((items) => {
            if(!items?.failed_deal_ids?.length) {
              this.toaster.success('Deals are sent successfully to AES');
            } else {
              const failed_deal_ids = items.failed_deal_ids.map(x => x.deal_id);
              this.toaster.warning(`The following deals have not been submitted succesfully, please refresh and retry individually: ${failed_deal_ids?.join(', ')}`);
            }
          }),
          catchError(error => {
            if (error === 'cancel' || error === 'backdrop click' || error === 'escape key press') return throwError(error)
            console.error('Unable to create AES export reports', error)
            this.toaster.error('Unable to create AES export reports, please refresh and retry individually')
            return of(createAESInBatchFailure({ error }))
          })))))

    setAesResponseStatus$ = createEffect(() => this.actions$.pipe(
      ofType(setAesResponseStatus),
      tap(({ status }) => this.toaster.progress(`Setting request status to ... ${status}`)),
      switchMap(action =>
        from(this.aes.setResponseStatus(action.reportId, action.status)).pipe(
          map(report => setAesResponseStatusSuccess(report)),
          tap(() => this.toaster.success('Updated response status successfully')),
          catchError(error => {
            console.error('Unable to set response status', error)
            if (!error.toasted) this.toaster.error('Unable to set response status', error.error || error )
            return of(setAesResponseStatusFailure({ error }))
          })))))
}
